import { useState, useRef, useEffect, useMemo } from 'react';
import {
  MdAdminPanelSettings,
  MdReply,
  MdClose,
  MdSend,
  MdAttachFile,
  MdPictureAsPdf,
  MdDescription,
  MdTableChart,
  MdInsertDriveFile,
  MdImage,
} from 'react-icons/md';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { renderTextWithMentions, insertMentionAtCursor, toBackendMentionFormat } from '../../utils/mentionUtils';
import { getUserId } from '../../utils/userIds';
import { resolveUserAvatarSrc } from '../../utils/avatarUrl';
import './CommentModal.css';

const API_URL =
  process.env.NODE_ENV === 'production'
    ? (process.env.REACT_APP_API_URL || window.location.origin)
    : 'http://localhost:5000';

const getInitials = (name) => name?.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);

const getFileIcon = (mimeType, originalName) => {
  const m = (mimeType || '').toLowerCase();
  const name = (originalName || '').toLowerCase();
  if (m.includes('pdf') || name.endsWith('.pdf')) return MdPictureAsPdf;
  if (m.includes('word') || m.includes('document') || name.endsWith('.doc') || name.endsWith('.docx')) return MdDescription;
  if (m.includes('sheet') || m.includes('excel') || name.endsWith('.xls') || name.endsWith('.xlsx') || name.endsWith('.csv')) return MdTableChart;
  if (m.startsWith('image/')) return MdImage;
  return MdInsertDriveFile;
};
const avatarColors = ['#6366f1', '#f59e0b', '#10b981', '#ef4444', '#3b82f6'];
const avatarColor = (name) => avatarColors[name?.charCodeAt(0) % avatarColors.length] || '#6366f1';

const COMMENT_REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '🙏', '👏', '✅', '👀'];

/** Aggregate reactions by emoji for display (aligned with Task Detail). */
const groupCommentReactions = (reactions, currentUserId) => {
  if (!Array.isArray(reactions) || reactions.length === 0) return [];
  const map = new Map();
  for (const r of reactions) {
    const em = r.emoji;
    if (!em) continue;
    const rid = r.user?._id ?? r.user;
    const name = r.user?.name || 'Someone';
    if (!map.has(em)) map.set(em, { emoji: em, count: 0, mine: false, names: [] });
    const row = map.get(em);
    row.count += 1;
    row.names.push(name);
    if (currentUserId != null && String(rid) === String(currentUserId)) row.mine = true;
  }
  return Array.from(map.values())
    .map((row) => ({ ...row, title: row.names.filter(Boolean).join(', ') }))
    .sort((a, b) => b.count - a.count);
};

/** Profile photo when available; initials only as fallback after failed load or no image */
const CommentAvatar = ({ user: u, size = 34 }) => {
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [u?._id, u?.id, u?.avatar]);

  const imgSrc = useMemo(
    () => (imgFailed || !u ? null : resolveUserAvatarSrc(u, API_URL)),
    [u, imgFailed],
  );

  if (!u) return null;

  return (
    <div
      className="comment-avatar"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.35,
        background: imgSrc ? 'transparent' : avatarColor(u.name),
        padding: 0,
        overflow: 'hidden',
      }}
      title={u.name}
    >
      {imgSrc ? (
        <img
          src={imgSrc}
          alt=""
          className="comment-avatar-img"
          onError={() => setImgFailed(true)}
        />
      ) : (
        <span className="comment-avatar-initials">{getInitials(u.name)}</span>
      )}
    </div>
  );
};

/** Merge roster `/users/list` data so comment authors show profile photos when populate omits avatar */
function enrichUserForAvatar(localUser, roster) {
  if (!localUser) return null;
  const id = getUserId(localUser);
  if (!id || !Array.isArray(roster)) return localUser;
  const match = roster.find((r) => String(getUserId(r)) === String(id));
  if (!match) return localUser;
  return {
    ...localUser,
    avatar: localUser.avatar || match.avatar,
    name: localUser.name || match.name,
  };
}

const CommentModal = ({ taskId, taskTitle, onClose, onUpdated }) => {
  const { user } = useAuth();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reactionBusyCommentId, setReactionBusyCommentId] = useState(null);
  const [text, setText] = useState('');
  const [replyTo, setReplyTo] = useState(null); // { _id, name } of comment author being replied to
  const [submitting, setSubmitting] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionStart, setMentionStart] = useState(-1);
  const [mentionSearch, setMentionSearch] = useState('');
  const bottomRef = useRef();
  const inputRef = useRef();
  const fileInputRef = useRef();
  const mentionListRef = useRef([]);
  const [selectedFile, setSelectedFile] = useState(null);

  const fetchTask = async () => {
    const { data } = await api.get(`/tasks/${taskId}`);
    setTask(data.data);
    setLoading(false);
  };

  useEffect(() => {
    fetchTask();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  useEffect(() => {
    setUsersLoading(true);
    api.get('/users/list').then(({ data }) => setUsers(data.data || [])).catch(() => setUsers([])).finally(() => setUsersLoading(false));
  }, []);

  useEffect(() => {
    if (!loading) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [task?.comments?.length, loading]);

  const handleReply = (comment) => {
    setReplyTo({ _id: comment.user?._id, name: comment.user?.name });
    setText('');
    setMentionOpen(false);
    inputRef.current?.focus();
  };

  const cancelReply = () => setReplyTo(null);

  const handleTextChange = (e) => {
    const val = e.target.value;
    const pos = e.target.selectionStart ?? val.length;
    setText(val);
    const beforeCursor = val.slice(0, pos);
    const atIdx = beforeCursor.lastIndexOf('@');
    if (atIdx !== -1 && (atIdx === 0 || /\s/.test(val[atIdx - 1]))) {
      setMentionStart(atIdx);
      setMentionSearch(beforeCursor.slice(atIdx + 1).toLowerCase());
      setMentionOpen(true);
    } else {
      setMentionOpen(false);
    }
  };

  const handleSelectMention = (u) => {
    const cursorPos = inputRef.current?.selectionStart ?? text.length;
    const countBefore = (text.slice(0, mentionStart).match(/@/g) || []).length;
    const list = mentionListRef.current;
    list.splice(countBefore, 0, { userId: u._id, displayName: u.name });
    const { newValue, newPos } = insertMentionAtCursor(text, mentionStart, cursorPos, u._id, u.name);
    setText(newValue);
    setMentionOpen(false);
    setMentionSearch('');
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const mentionFiltered = mentionSearch
    ? users.filter((u) => u._id !== user?._id && (u.name || '').toLowerCase().includes(mentionSearch))
    : users.filter((u) => u._id !== user?._id);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setSelectedFile(null);
      return;
    }
    setSelectedFile(file);
  };

  const clearFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDownloadAttachment = (filename, originalName) => {
    api
      .get(`/tasks/files/${encodeURIComponent(filename)}?name=${encodeURIComponent(originalName || filename)}`, {
        responseType: 'blob',
      })
      .then((res) => {
        const url = window.URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = originalName || filename;
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => toast.error('Download failed'));
  };

  const handleToggleCommentReaction = async (commentId, emoji) => {
    const cid = commentId != null ? String(commentId) : '';
    if (!cid || reactionBusyCommentId) return;
    setReactionBusyCommentId(cid);
    try {
      await api.post(`/tasks/${taskId}/comments/${cid}/reactions`, { emoji });
      await fetchTask();
      if (typeof onUpdated === 'function') onUpdated();
    } catch {
      toast.error('Could not update reaction');
    } finally {
      setReactionBusyCommentId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim() && !selectedFile) return;
    setSubmitting(true);
    try {
      const textForApi = toBackendMentionFormat(text.trim(), mentionListRef.current);
      if (selectedFile) {
        const fd = new FormData();
        fd.append('file', selectedFile);
        if (textForApi) fd.append('text', textForApi);
        if (replyTo) fd.append('replyTo', replyTo._id);
        await api.post(`/tasks/${taskId}/comments/attachments`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        const payload = { text: textForApi };
        if (replyTo) payload.replyTo = replyTo._id;
        await api.post(`/tasks/${taskId}/comments`, payload);
      }
      setText('');
      setReplyTo(null);
      mentionListRef.current = [];
      clearFile();
      await fetchTask();
      if (typeof onUpdated === 'function') {
        onUpdated();
      }
    } catch {
      toast.error('Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal comment-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3>Comments</h3>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>📋 {taskTitle}</p>
          </div>
          <button className="btn-icon" onClick={onClose}><MdClose /></button>
        </div>

        <div className="comment-list">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 32 }}><div className="spinner" /></div>
          ) : !task?.comments?.length ? (
            <div style={{ textAlign: 'center', padding: '32px 20px', color: 'var(--text-muted)' }}>
              <MdAdminPanelSettings size={36} />
              <p style={{ marginTop: 8 }}>No comments yet. Start the conversation!</p>
            </div>
          ) : (
            task.comments.map((c, i) => {
              const authorId = getUserId(c.user);
              const currentUserId = getUserId(user);
              const isMine =
                currentUserId != null &&
                authorId != null &&
                String(authorId) === String(currentUserId);
              const prev = i > 0 ? task.comments[i - 1] : null;
              const prevAuthorId = prev ? getUserId(prev.user) : null;
              const isGrouped =
                prev &&
                authorId != null &&
                prevAuthorId != null &&
                String(authorId) === String(prevAuthorId);
              const reactionGroups = groupCommentReactions(
                c.reactions,
                currentUserId,
              );
              const createdAt = new Date(c.createdAt);
              const commentDateTime = format(createdAt, "MMM d, yyyy 'at' HH:mm");
              return (
                <div
                  key={c._id ?? i}
                  className={`comment-row ${c.isAdminComment ? 'comment-row--admin' : ''} ${isMine ? 'comment-row--mine' : ''} ${isGrouped ? 'comment-row--grouped' : ''}`}
                >
                  {!isMine &&
                    (isGrouped ? (
                      <div className="comment-avatar-spacer" aria-hidden />
                    ) : (
                      <CommentAvatar user={enrichUserForAvatar(c.user, users)} size={34} />
                    ))}
                  <div className="comment-stack">
                    {!isMine && !isGrouped && (
                      <div className="comment-sender">
                        <span className="comment-sender-name">{c.user?.name}</span>
                        {c.user?.role === 'admin' && (
                          <span className="comment-admin-badge"><MdAdminPanelSettings size={11} /> Admin</span>
                        )}
                      </div>
                    )}
                    <div
                      className={`comment-bubble ${isMine ? 'comment-bubble--sent' : 'comment-bubble--received'} ${c.isAdminComment ? 'comment-bubble--admin' : ''}`}
                    >
                      {c.replyTo && (
                        <div className="comment-reply-quote">
                          <MdReply size={14} aria-hidden />
                          <span className="comment-reply-quote-label">{c.replyTo?.name}</span>
                        </div>
                      )}
                      {!!c.text && (
                        <div className={`comment-text ${c.isAdminComment ? 'admin-text' : ''}`}>
                          {renderTextWithMentions(c.text, taskId)}
                        </div>
                      )}
                      {Array.isArray(c.attachments) && c.attachments.length > 0 && (
                        <div className="comment-attachments">
                          {c.attachments.map((att, idx) => {
                            const isImage = (att.mimeType || '').startsWith('image/');
                            const fileUrl = att.url.startsWith('http') ? att.url : `${API_URL}${att.url}`;
                            const FileIcon = getFileIcon(att.mimeType, att.originalName);
                            return (
                              <div key={idx} className="comment-attachment-item">
                                {isImage ? (
                                  <>
                                    <div className="comment-attachment-preview">
                                      <img src={fileUrl} alt={att.originalName} />
                                    </div>
                                    <button
                                      type="button"
                                      className="comment-attachment-download"
                                      onClick={() => handleDownloadAttachment(att.filename, att.originalName)}
                                    >
                                      <MdAttachFile size={14} /> Download
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    type="button"
                                    className="comment-attachment-link"
                                    onClick={() => handleDownloadAttachment(att.filename, att.originalName)}
                                  >
                                    <FileIcon size={20} className="comment-attachment-icon" />
                                    <span className="comment-attachment-name">{att.originalName}</span>
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <div className="comment-bubble-footer">
                        <time
                          className="comment-time-in-bubble"
                          dateTime={createdAt.toISOString()}
                        >
                          {commentDateTime}
                        </time>
                      </div>
                    </div>
                    <div className="comment-toolbar" dir="ltr">
                      <div className="comment-reactions">
                        {reactionGroups.length > 0 && (
                          <div className="comment-reaction-chips" role="group" aria-label="Reactions on this comment">
                            {reactionGroups.map((rg) => (
                              <button
                                key={rg.emoji}
                                type="button"
                                className={`comment-reaction-chip${rg.mine ? ' comment-reaction-chip--mine' : ''}`}
                                title={rg.title}
                                disabled={!!reactionBusyCommentId}
                                onClick={() => handleToggleCommentReaction(c._id, rg.emoji)}
                              >
                                <span className="comment-reaction-chip-emoji" aria-hidden>{rg.emoji}</span>
                                <span className="comment-reaction-chip-count">{rg.count}</span>
                              </button>
                            ))}
                          </div>
                        )}
                        <div className="comment-reaction-picker" role="toolbar" aria-label="Add a reaction">
                          {COMMENT_REACTION_EMOJIS.map((emoji) => (
                            <button
                              key={emoji}
                              type="button"
                              className="comment-reaction-add"
                              title={`React with ${emoji}`}
                              disabled={!!reactionBusyCommentId}
                              onClick={() => handleToggleCommentReaction(c._id, emoji)}
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </div>
                      <button type="button" className="comment-reply-btn" onClick={() => handleReply(c)}>
                        <MdReply size={13} /> Reply
                      </button>
                    </div>
                  </div>
                  {isMine && <CommentAvatar user={enrichUserForAvatar(user, users)} size={34} />}
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>

        {/* Reply / attachment indicators */}
        {replyTo && (
          <div className="reply-indicator">
            <MdReply size={14} />
            <span>Replying to <strong>{replyTo.name}</strong></span>
            <button onClick={cancelReply}><MdClose size={13} /></button>
          </div>
        )}
        {selectedFile && (
          <div className="reply-indicator">
            <MdAttachFile size={14} />
            <span>Attaching <strong>{selectedFile.name}</strong></span>
            <button onClick={clearFile}><MdClose size={13} /></button>
          </div>
        )}

        {/* Hidden file input for attachments */}
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />

        {/* Input row */}
        <form className="comment-input-row" onSubmit={handleSubmit}>
          <div className="comment-input-avatar-wrap">
            <CommentAvatar user={enrichUserForAvatar(user, users)} size={32} />
          </div>
          <button
            type="button"
            className="comment-attach-btn"
            onClick={() => fileInputRef.current?.click()}
            title="Attach file"
          >
            <MdAttachFile size={18} />
          </button>
          <div className="comment-input-wrap">
            <input
              ref={inputRef}
              className="comment-input"
              value={text}
              onChange={handleTextChange}
              onBlur={() => setTimeout(() => setMentionOpen(false), 200)}
              placeholder={replyTo ? `Reply to ${replyTo.name}... (type @ to mention)` : 'Write a comment... (type @ to mention)'}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
            />
            {mentionOpen && (
              <ul className="mention-dropdown" role="listbox">
                {usersLoading ? (
                  <li className="mention-dropdown-item" style={{ cursor: 'default', color: 'var(--text-muted)' }}>
                    Loading users…
                  </li>
                ) : mentionFiltered.length === 0 ? (
                  <li className="mention-dropdown-item" style={{ cursor: 'default', color: 'var(--text-muted)' }}>
                    No users match
                  </li>
                ) : (
                  mentionFiltered.map((u) => (
                    <li
                      key={u._id}
                      role="option"
                      className="mention-dropdown-item"
                      onMouseDown={(e) => { e.preventDefault(); handleSelectMention(u); }}
                    >
                      <CommentAvatar user={u} size={28} />
                      <span>{u.name}</span>
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>
          <button
            type="submit"
            className="comment-send-btn"
            disabled={submitting || (!text.trim() && !selectedFile)}
          >
            <MdSend size={18} />
          </button>
        </form>
      </div>
    </div>
  );
};

export default CommentModal;
